import React from "react";
import { Card, Confirm } from "ktm-ui";
import toastr from "toastr";

interface Props {
  policy_id: string;
}

interface Advisor {
  id: string;
  name: string;
  advisorId?: string;
  relationship?: string;
}

interface State {
  loading: boolean;
  editingDetails: boolean;
  editingAdvisors: boolean;
  enabled: boolean;
  natPolicy: string;
  corehealthNhcRate: number;
  bank: { transitNo: string; bankNo: string; accountNo: string };
  bankConfirm: { transitNo: string; bankNo: string; accountNo: string };
  selectedAdvisor1: Advisor | null;
  selectedAdvisor2: Advisor | null;
  selectedAdvisor3: Advisor | null;
  advisor1Rate: any;
  advisor2Rate: any;
  advisor3Rate: any;
  search1: string;
  search2: string;
  search3: string;
  advisors1: Advisor[];
  advisors2: Advisor[];
  advisors3: Advisor[];
  originalDetails: any;
  originalAdvisors: any;
  originalAdvisorRates: any;
  showAdvisorConfirm: boolean;
}

export default class CorehealthDetailsCard extends React.Component<Props, State> {
  state: State = {
    loading: true,
    editingDetails: false,
    editingAdvisors: false,
    enabled: false,
    natPolicy: "NAT1001",
    corehealthNhcRate: 0,
    bank: { transitNo: "", bankNo: "", accountNo: "" },
    bankConfirm: { transitNo: "", bankNo: "", accountNo: "" },
    selectedAdvisor1: null,
    selectedAdvisor2: null,
    selectedAdvisor3: null,
    advisor1Rate: null,
    advisor2Rate: null,
    advisor3Rate: null,
    search1: "",
    search2: "",
    search3: "",
    advisors1: [],
    advisors2: [],
    advisors3: [],
    originalDetails: null,
    originalAdvisors: null,
    originalAdvisorRates: null,
    showAdvisorConfirm: false,
  };

  componentDidMount() {
    this.loadData();
  }

  loadData = async () => {
    const { policy_id } = this.props;
    const detailsRes = await fetch(`/api/Corehealth/getdetails?policyId=${policy_id}`);
    if (!detailsRes.ok) {
      console.error("Failed to load corehealth details");
      return;
    }
    const details = await detailsRes.json();

    const advisorsRes = await fetch(`/api/Corehealth/getadvisors?policyId=${policy_id}`);
    if (!advisorsRes.ok) {
      console.error("Failed to load corehealth advisors");
      return;
    }
    const advisors = await advisorsRes.json();

    this.setState({
      enabled: details.enabled,
      natPolicy: details.natPolicy,
      corehealthNhcRate: details.corehealthNhcRate,
      bank: {
        transitNo: details.transitNo || "",
        bankNo: details.bankNo || "",
        accountNo: details.accountNo || "",
      },
      selectedAdvisor1: advisors.corehealthAdvisor1
        ? { id: advisors.corehealthAdvisor1, name: advisors.corehealthAdvisor1Name }
        : null,
      advisor1Rate: advisors.corehealthAdvisor1Rate,
      selectedAdvisor2: advisors.corehealthAdvisor2
        ? { id: advisors.corehealthAdvisor2, name: advisors.corehealthAdvisor2Name }
        : null,
      advisor2Rate: advisors.corehealthAdvisor2Rate,
      selectedAdvisor3: advisors.corehealthAdvisor3
        ? { id: advisors.corehealthAdvisor3, name: advisors.corehealthAdvisor3Name }
        : null,
      advisor3Rate: advisors.corehealthAdvisor3Rate,
      loading: false,
    });
  };

  startEditDetails = () => {
    this.setState({
      editingDetails: true,
      bankConfirm: { transitNo: "", bankNo: "", accountNo: "" },
      originalDetails: {
        enabled: this.state.enabled,
        natPolicy: this.state.natPolicy,
        corehealthNhcRate: this.state.corehealthNhcRate,
        bank: { ...this.state.bank },
      },
    });
  };

  cancelEditDetails = () => {
    if (this.state.originalDetails) {
      this.setState({
        editingDetails: false,
        enabled: this.state.originalDetails.enabled,
        natPolicy: this.state.originalDetails.natPolicy,
        corehealthNhcRate: this.state.originalDetails.corehealthNhcRate,
        bank: { ...this.state.originalDetails.bank },
        bankConfirm: { transitNo: "", bankNo: "", accountNo: "" },
        originalDetails: null,
      });
    } else {
      this.setState({
        editingDetails: false,
        bankConfirm: { transitNo: "", bankNo: "", accountNo: "" },
      });
    }
  };

  saveDetails = async () => {
    const { transitNo, bankNo, accountNo } = this.state.bank;
    const anyFilled = transitNo?.trim() || bankNo?.trim() || accountNo?.trim();
    const allFilled = transitNo?.trim() && bankNo?.trim() && accountNo?.trim();
    if (this.state.enabled && anyFilled && !allFilled) {
      toastr.error("Please enter the complete bank information", "Error");
      return;
    }

    if (this.state.enabled && allFilled) {
      const t = transitNo.trim();
      const b = bankNo.trim();
      const a = accountNo.trim();
      if (!/^\d{5}$/.test(t)) {
        toastr.error("Transit No must be exactly 5 digits", "Error");
        return;
      }
      if (!/^\d{3}$/.test(b)) {
        toastr.error("Bank No must be exactly 3 digits", "Error");
        return;
      }
      if (!/^\d{7,12}$/.test(a)) {
        toastr.error("Account No must be 7-12 digits", "Error");
        return;
      }

      const originalBank = this.state.originalDetails?.bank ?? { transitNo: "", bankNo: "", accountNo: "" };
      const confirm = this.state.bankConfirm;
      if (t !== originalBank.transitNo && t !== confirm.transitNo.trim()) {
        toastr.error("Transit No confirmation does not match", "Error");
        return;
      }
      if (b !== originalBank.bankNo && b !== confirm.bankNo.trim()) {
        toastr.error("Bank No confirmation does not match", "Error");
        return;
      }
      if (a !== originalBank.accountNo && a !== confirm.accountNo.trim()) {
        toastr.error("Account No confirmation does not match", "Error");
        return;
      }
    }

    const body = {
      PolicyId: this.props.policy_id,
      Enabled: this.state.enabled,
      BankNo: bankNo?.trim() || null,
      TransitNo: transitNo?.trim() || null,
      AccountNo: accountNo?.trim() || null,
      natPolicy: this.state.natPolicy?.trim() || null,
      corehealthNhcRate: this.state.corehealthNhcRate || 0,
    };

    const note =
      `Enabled: ${this.state.enabled} ` +
      (bankNo?.trim() ? `BankNo: ${bankNo.trim()} ` : "") +
      (transitNo?.trim() ? `TransitNo: ${transitNo.trim()} ` : "") +
      (accountNo?.trim() ? `AccountNo: ${accountNo.trim()} ` : "") +
      (this.state.natPolicy?.trim() ? `NatPolicy: ${this.state.natPolicy.trim()} ` : "") +
      (this.state.corehealthNhcRate ? `NhcRate: ${this.state.corehealthNhcRate}` : "");

    try {
      const res = await fetch("/api/Corehealth/updatedetails", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("save failed");
      fetch("/api/PlanAdmin/updatepolicylogs", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Note: note,
          Action: "Policy Edit Corehealth Details",
          PolicyId: this.props.policy_id,
        }),
      });
      toastr.success("Corehealth details updated successfully");
      this.setState({ editingDetails: false, originalDetails: null });
    } catch (e) {
      console.error("Error saving Corehealth details:", e);
      toastr.error("Failed to save Corehealth details. Please try again.", "Error");
    }
  };

  startEditAdvisors = () => {
    this.setState({
      editingAdvisors: true,
      originalAdvisors: {
        selectedAdvisor1: this.state.selectedAdvisor1,
        selectedAdvisor2: this.state.selectedAdvisor2,
        selectedAdvisor3: this.state.selectedAdvisor3,
      },
      originalAdvisorRates: {
        advisor1Rate: this.state.advisor1Rate,
        advisor2Rate: this.state.advisor2Rate,
        advisor3Rate: this.state.advisor3Rate,
      },
      search1: this.state.selectedAdvisor1?.name || "",
      search2: this.state.selectedAdvisor2?.name || "",
      search3: this.state.selectedAdvisor3?.name || "",
    });
  };

  cancelEditAdvisors = () => {
    if (this.state.originalAdvisors && this.state.originalAdvisorRates) {
      this.setState({
        editingAdvisors: false,
        selectedAdvisor1: this.state.originalAdvisors.selectedAdvisor1,
        selectedAdvisor2: this.state.originalAdvisors.selectedAdvisor2,
        selectedAdvisor3: this.state.originalAdvisors.selectedAdvisor3,
        advisor1Rate: this.state.originalAdvisorRates.advisor1Rate,
        advisor2Rate: this.state.originalAdvisorRates.advisor2Rate,
        advisor3Rate: this.state.originalAdvisorRates.advisor3Rate,
        originalAdvisors: null,
        originalAdvisorRates: null,
        search1: "",
        search2: "",
        search3: "",
        advisors1: [],
        advisors2: [],
        advisors3: [],
      });
    } else {
      this.setState({
        editingAdvisors: false,
        search1: "",
        search2: "",
        search3: "",
        advisors1: [],
        advisors2: [],
        advisors3: [],
      });
    }
  };

  saveAdvisors = () => {
    const a1 = this.state.selectedAdvisor1;
    const a2 = this.state.selectedAdvisor2;
    const a3 = this.state.selectedAdvisor3;
    if (
      (a1 && a2 && a1.id === a2.id) ||
      (a1 && a3 && a1.id === a3.id) ||
      (a2 && a3 && a2.id === a3.id)
    ) {
      toastr.error("Advisor already assigned", "Error");
      return;
    }

    this.setState({ showAdvisorConfirm: true });
  };

  handleAdvisorConfirmClose = (result: { continue: boolean }) => {
    this.setState({ showAdvisorConfirm: false });
    if (result.continue) {
      this.doSaveAdvisors();
    }
  };

  doSaveAdvisors = async () => {
    const a1 = this.state.selectedAdvisor1;
    const a2 = this.state.selectedAdvisor2;
    const a3 = this.state.selectedAdvisor3;

    const body = {
      Id: this.props.policy_id,
      CorehealthAdvisor1: a1 ? a1.id : null,
      CorehealthAdvisor2: a2 ? a2.id : null,
      CorehealthAdvisor3: a3 ? a3.id : null,
      CorehealthAdvisor1Rate: parseFloat(this.state.advisor1Rate) || 0,
      CorehealthAdvisor2Rate: parseFloat(this.state.advisor2Rate) || 0,
      CorehealthAdvisor3Rate: parseFloat(this.state.advisor3Rate) || 0,
    };

    const note =
      (a1 ? `Advisor1: ${a1.name} ` : "") +
      (a2 ? `Advisor2: ${a2.name} ` : "") +
      (a3 ? `Advisor3: ${a3.name} ` : "") +
      `Advisor1Rate: ${parseFloat(this.state.advisor1Rate) || 0} ` +
      `Advisor2Rate: ${parseFloat(this.state.advisor2Rate) || 0} ` +
      `Advisor3Rate: ${parseFloat(this.state.advisor3Rate) || 0}`;

    await fetch("/api/corehealth/setadvisors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(() => {
      fetch("/api/PlanAdmin/updatepolicylogs", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Note: note,
          Action: "Policy Edit Corehealth Advisors",
          PolicyId: this.props.policy_id,
        }),
      });
    });

    toastr.success("Corehealth advisors updated successfully");
    this.setState({ editingAdvisors: false });
  };

  handleAdvisorSearch = async (term: string, n: 1 | 2 | 3) => {
    const res = await fetch(`/api/corehealth/searchadvisors?search=${term}`);
    const advisors = await res.json();
    this.setState({ [`advisors${n}`]: advisors } as any);
    if (advisors.length === 1) {
      this.selectAdvisor(advisors[0], n);
    }
  };

  selectAdvisor = (advisor: Advisor, n: 1 | 2 | 3) => {
    this.setState({
      [`selectedAdvisor${n}`]: advisor,
      [`search${n}`]: advisor.name,
    } as any);
  };

  setRate = (n: 1 | 2 | 3, rate: string) => {
    this.setState({ [`advisor${n}Rate`]: rate } as any);
  };

  render() {
    if (this.state.loading) {
      return (
        <Card label="Corehealth Details">
          <div style={{ padding: 20, textAlign: "center", color: "#888" }}>Loading...</div>
        </Card>
      );
    }

    const isActive = this.state.enabled;
    const editing = this.state.editingDetails;

    const detailsActions = editing ? (
      <>
        <button className="RT-Link" onClick={this.cancelEditDetails}>Cancel</button>
        <button className="RT-Link" onClick={this.saveDetails}>Save</button>
      </>
    ) : (
      <a href="#" className="RT-Link" onClick={(e) => { e.preventDefault(); this.startEditDetails(); }}>Edit</a>
    );

    return (
      <>
      <Card
        label="Corehealth Details"
        background={isActive ? "active-card" : undefined}
        actions={detailsActions}
      >
        <div className="RT-Line">
          <div className="RT-Line__content RT-Line__content--space-evenly">
            <div className="RT-Line__item">
              <div className="RT-Static-Field">
                <label className="RT-Static-Field__label">Corehealth Enabled</label>
                <div className="RT-Static-Field__content">
                  {editing ? (
                    <select
                      className="RT-TextBox__select"
                      value={this.state.enabled ? "true" : "false"}
                      onChange={(e) => this.setState({ enabled: e.target.value === "true" })}
                    >
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </select>
                  ) : (
                    this.state.enabled ? "Enabled" : "Disabled"
                  )}
                </div>
              </div>
            </div>
            {(isActive || editing) && (
              <>
                <div className="RT-Line__item">
                  <div className="RT-Static-Field">
                    <label className="RT-Static-Field__label">Nat Policy</label>
                    <div className="RT-Static-Field__content">NAT1001 (Corehealth)</div>
                  </div>
                </div>
                <div className="RT-Line__item">
                  <div className="RT-Static-Field">
                    <label className="RT-Static-Field__label">NHC Rate</label>
                    <div className="RT-Static-Field__content">
                      {editing ? (
                        <input
                          type="number"
                          className="RT-TextBox__input"
                          value={this.state.corehealthNhcRate}
                          onChange={(e) => this.setState({ corehealthNhcRate: parseFloat(e.target.value) })}
                          min="0"
                          step="0.01"
                          placeholder="Enter NHC Rate"
                        />
                      ) : (
                        `${this.state.corehealthNhcRate}%`
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {(isActive || editing) && (
          <div className="RT-Line">
            <div className="RT-Line__content RT-Line__content--space-evenly">
              <div className="RT-Line__item">
                <div className="RT-Static-Field">
                  <label className="RT-Static-Field__label">Bank Transit No.</label>
                  <div className="RT-Static-Field__content">
                    {editing ? (
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={5}
                        className="RT-TextBox__input"
                        value={this.state.bank.transitNo}
                        onChange={(e) => this.setState({ bank: { ...this.state.bank, transitNo: e.target.value.replace(/\D/g, "").slice(0, 5) } })}
                      />
                    ) : (
                      this.state.bank.transitNo
                    )}
                  </div>
                </div>
              </div>
              <div className="RT-Line__item">
                <div className="RT-Static-Field">
                  <label className="RT-Static-Field__label">Bank No.</label>
                  <div className="RT-Static-Field__content">
                    {editing ? (
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={3}
                        className="RT-TextBox__input"
                        value={this.state.bank.bankNo}
                        onChange={(e) => this.setState({ bank: { ...this.state.bank, bankNo: e.target.value.replace(/\D/g, "").slice(0, 3) } })}
                      />
                    ) : (
                      this.state.bank.bankNo
                    )}
                  </div>
                </div>
              </div>
              <div className="RT-Line__item">
                <div className="RT-Static-Field">
                  <label className="RT-Static-Field__label">Bank Account No.</label>
                  <div className="RT-Static-Field__content">
                    {editing ? (
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={12}
                        className="RT-TextBox__input"
                        value={this.state.bank.accountNo}
                        onChange={(e) => this.setState({ bank: { ...this.state.bank, accountNo: e.target.value.replace(/\D/g, "").slice(0, 12) } })}
                      />
                    ) : (
                      this.state.bank.accountNo
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {editing && (() => {
          const originalBank = this.state.originalDetails?.bank ?? { transitNo: "", bankNo: "", accountNo: "" };
          const transitChanged = this.state.bank.transitNo !== originalBank.transitNo;
          const bankChanged = this.state.bank.bankNo !== originalBank.bankNo;
          const accountChanged = this.state.bank.accountNo !== originalBank.accountNo;
          if (!transitChanged && !bankChanged && !accountChanged) return null;
          return (
          <div className="RT-Line">
            <div className="RT-Line__content RT-Line__content--space-evenly">
              <div className="RT-Line__item">
                {transitChanged ? (
                <div className="RT-Static-Field">
                  <label className="RT-Static-Field__label">Confirm Transit No.</label>
                  <div className="RT-Static-Field__content">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={5}
                      className="RT-TextBox__input"
                      value={this.state.bankConfirm.transitNo}
                      onChange={(e) => this.setState({ bankConfirm: { ...this.state.bankConfirm, transitNo: e.target.value.replace(/\D/g, "").slice(0, 5) } })}
                    />
                  </div>
                </div>
                ) : null}
              </div>
              <div className="RT-Line__item">
                {bankChanged ? (
                <div className="RT-Static-Field">
                  <label className="RT-Static-Field__label">Confirm Bank No.</label>
                  <div className="RT-Static-Field__content">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={3}
                      className="RT-TextBox__input"
                      value={this.state.bankConfirm.bankNo}
                      onChange={(e) => this.setState({ bankConfirm: { ...this.state.bankConfirm, bankNo: e.target.value.replace(/\D/g, "").slice(0, 3) } })}
                    />
                  </div>
                </div>
                ) : null}
              </div>
              <div className="RT-Line__item">
                {accountChanged ? (
                <div className="RT-Static-Field">
                  <label className="RT-Static-Field__label">Confirm Account No.</label>
                  <div className="RT-Static-Field__content">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={12}
                      className="RT-TextBox__input"
                      value={this.state.bankConfirm.accountNo}
                      onChange={(e) => this.setState({ bankConfirm: { ...this.state.bankConfirm, accountNo: e.target.value.replace(/\D/g, "").slice(0, 12) } })}
                    />
                  </div>
                </div>
                ) : null}
              </div>
            </div>
          </div>
          );
        })()}

        {isActive && this.renderAdvisorsSection()}
      </Card>
      {this.state.showAdvisorConfirm && (
        <Confirm
          message="Does the Primary Advisor also need updating?"
          $onClose={this.handleAdvisorConfirmClose}
          $layerZIndex={1000}
        />
      )}
      </>
    );
  }

  renderAdvisorsSection() {
    const editing = this.state.editingAdvisors;
    const advisorsActions = editing ? (
      <>
        <button className="RT-Link" onClick={this.cancelEditAdvisors}>Cancel</button>
        <button className="RT-Link" onClick={this.saveAdvisors}>Save</button>
      </>
    ) : (
      <a href="#" className="RT-Link" onClick={(e) => { e.preventDefault(); this.startEditAdvisors(); }}>Edit</a>
    );

    return (
      <div className="product-subsection">
        <div className="product-subsection__header">
          <div className="product-subsection__title">Corehealth Advisors</div>
          <div>{advisorsActions}</div>
        </div>
        <div className="RT-Line">
          <div className="RT-Line__content RT-Line__content--space-evenly">
            <div className="RT-Line__item">
              <div className="RT-Static-Field">
                <label className="RT-Static-Field__label">Advisor 1 (Primary)</label>
                <div className="RT-Static-Field__content">
                  {editing ? (
                    <>
                      <input
                        className="RT-TextBox__input"
                        type="text"
                        value={this.state.search1}
                        onChange={(e) => this.setState({ search1: e.target.value })}
                        onBlur={() => this.handleAdvisorSearch(this.state.search1, 1)}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") this.handleAdvisorSearch(this.state.search1, 1);
                        }}
                      />
                      {this.state.advisors1.length > 0 && (
                        <select
                          className="RT-TextBox__select custom-dropdown"
                          onChange={(e) =>
                            this.selectAdvisor(this.state.advisors1[e.target.selectedIndex - 1], 1)
                          }
                        >
                          <option className="dropdown-header" disabled selected>
                            Advisor Name - Advisor ID (Relationship)
                          </option>
                          {this.state.advisors1.map((advisor, index) => (
                            <option key={index} value={index + 1}>
                              {advisor.name} - {advisor.advisorId} - {advisor.relationship}
                            </option>
                          ))}
                        </select>
                      )}
                    </>
                  ) : (
                    <p>{this.state.selectedAdvisor1 ? this.state.selectedAdvisor1.name : "No advisor selected"}</p>
                  )}
                </div>
              </div>
            </div>
            {this.state.selectedAdvisor1 && (
              <div className="RT-Line__item">
                <div className="RT-Static-Field">
                  <label className="RT-Static-Field__label">Advisor 1 Rate</label>
                  <div className="RT-Static-Field__content">
                    {editing ? (
                      <input
                        type="number"
                        className="RT-TextBox__input"
                        value={this.state.advisor1Rate || "0"}
                        onChange={(e) => this.setRate(1, e.target.value)}
                        min="0"
                        max="100"
                        step="0.01"
                        placeholder="Enter % rate"
                      />
                    ) : (
                      <p>{this.state.advisor1Rate ? `${this.state.advisor1Rate}%` : "No rate set"}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}
